import { IRefIds } from 'constants/refs';
import {
  fetchCard,
  fetchCards,
  fetchComponents,
  fetchStats,
  rebuildCard,
  wizardApply,
  wizardScan,
} from 'modules/Hanzi/api';
import {
  Card,
  CardItem,
  CardRow,
  ComponentRow,
  MissingItem,
  PopulateReport,
  ScanResult,
  ScanRow,
  STATUS_LABELS,
  Stats,
  cardIdFromNum,
  GF_ID_BASE,
} from 'modules/Hanzi/types';
import { BaseModel, IModelOptions, IModuleSearch } from './BaseModel';
import TranslationManagerModel from './TranslationManagerModel';

export type HanziMode = 'cards' | 'components' | 'wizard';

type Row = CardRow | ComponentRow | ScanRow;

/**
 * One model per tab of the Hanzi module. The grid changes with the mode (characters, GF
 * components, wizard scan); the item is always a card, read-only. In the Components tab the
 * item also carries the names of the component for the learner (`sources`, translations of
 * ref 112), edited with the shared Translations editor and saved here; no new/delete.
 */
export default class HanziModel extends BaseModel<Row, CardItem | MissingItem> {
  mode: HanziMode;
  stats: Stats | null = null;
  scan: ScanResult | null = null;
  report: PopulateReport | null = null;
  applying = false;
  /** item pane: flat view (down to the norm) or the direct pieces of the sources */
  flat = true;
  showOrder = false;
  /** names of the component for the learner (ref 112, item = GF number) */
  names = new TranslationManagerModel({ ref: IRefIds.hanziComponentName });

  constructor(options: IModelOptions & { mode: HanziMode }) {
    super(options);
    this.mode = options.mode;
    this.ref = IRefIds.hanzi;
    this.gridName = { cards: 'Hanzi', components: 'HanziComponents', wizard: 'HanziWizard' }[options.mode];
    this.getGridColumns();
    if (options.mode !== 'wizard') {
      this.getGrid({});
    }
    if (options.mode === 'cards') {
      this.loadStats();
    }
  }

  async getGrid(data: IModuleSearch = {}) {
    await super.getGrid();
    if (this.mode === 'cards') {
      const page = await fetchCards(data.text ?? '');
      this.gridData = page?.rows ?? [];
    } else if (this.mode === 'components') {
      this.gridData = (await fetchComponents()) ?? [];
    } else {
      this.scan = await wizardScan();
      this.gridData = this.scan?.rows ?? [];
    }
    this.loadingGrid = false;
    this.render();
  }

  async search(data: IModuleSearch) {
    await this.getGrid(data);
  }

  async reload() {
    await super.reload();
    await this.getGrid({});
    if (this.mode === 'cards') {
      await this.loadStats();
    }
  }

  async loadStats() {
    this.stats = await fetchStats();
    this.render();
  }

  async getGridRow(itemId: number) {
    if (this.mode !== 'cards') {
      return { id: itemId };
    }
    const page = await fetchCards(cardIdFromNum(itemId), 1);
    return page?.rows[0] ?? { id: itemId };
  }

  async loadItemData(itemId: number): Promise<CardItem | MissingItem> {
    const card = await fetchCard(itemId);
    const item: CardItem | MissingItem = card
      ? HanziModel.toItem(card)
      : { id: itemId, cardId: cardIdFromNum(itemId), missing: true };
    const seq = this.mode === 'components' ? HanziModel.gfSeqOf(item) : null;
    if (seq !== null) {
      item.gfSeq = seq;
      item.sources = await this.names.get(seq);
    }
    return item;
  }

  /** GF number of a card or of a component with no card (gf-<seq>). */
  static gfSeqOf(item: CardItem | MissingItem): number | null {
    if (item.gfSeq !== undefined && item.gfSeq !== null) {
      return item.gfSeq;
    }
    return item.id >= GF_ID_BASE ? item.id - GF_ID_BASE : null;
  }

  /** Saves the names of the component (the card itself is read-only). */
  async save() {
    await super.save();
    const item = this.itemUpdated as CardItem | MissingItem | undefined;
    const seq = item ? HanziModel.gfSeqOf(item) : null;
    if (item && seq !== null && item.sources) {
      await this.names.update(seq, item.sources);
    }
    await this.loadItemEdit(this.itemId);
  }

  static toItem(card: Card): CardItem {
    const flags = [
      card.complete ? 'complete' : 'incomplete',
      card.inStandard ? 'in the standard' : 'outside the standard',
      card.origin === 'synthetic' ? 'synthetic drawing' : '',
      card.origin === 'none' ? 'no data' : '',
    ].filter(Boolean);
    const typeLabel = card.componentOnly
      ? 'component only'
      : card.gfSeq !== null
      ? 'character · GF component'
      : 'character';
    return {
      ...card,
      missing: false,
      structureLabel: STATUS_LABELS[card.idsStatus],
      typeLabel,
      radicalLabel: card.radical
        ? `${card.radical}${card.radicalIndex !== null ? ` (Kangxi ${card.radicalIndex})` : ''}`
        : '',
      drawnFrom: card.referenceHost
        ? `${card.referenceHost} · strokes ${(card.referenceStrokes ?? []).join(', ')}`
        : '',
      flags: flags.join(' · '),
    };
  }

  /** Regenerates the current card from the sources and refreshes its grid row. */
  async rebuild() {
    const item = this.itemUpdated as CardItem | MissingItem | undefined;
    if (!item || item.missing) {
      return;
    }
    this.loadingItem = true;
    this.render();
    const card = await rebuildCard(item.cardId);
    await this.setItem(card ? HanziModel.toItem(card) : item);
    await this.updateGridRow(item.id, 'update');
    this.render();
  }

  toggleFlat() {
    this.flat = !this.flat;
    this.render();
  }

  toggleOrder() {
    this.showOrder = !this.showOrder;
    this.render();
  }

  // ---------- wizard ----------

  missingChars(): string[] {
    return (this.scan?.rows ?? []).filter((r) => !r.hasCard).map((r) => r.chr);
  }

  async apply(apply: boolean) {
    this.applying = true;
    this.render();
    this.report = await wizardApply(this.missingChars(), apply);
    this.applying = false;
    this.render();
    if (apply) {
      await this.getGrid({});
    }
  }
}
